import type { Promisable } from "type-fest";

import type {
  AssessmentId,
  DeleteAssessmentRequest,
  PersonState,
} from "@thalamos/haven-common";

import type { ProgressReading } from "../../api/formDraft/readAssessmentProgress.js";
import {
  AssessmentHistory,
  type GuestSharing,
  type HistoryPaging,
} from "../../components/AssessmentHistory/AssessmentHistory.js";
import type { FinishedAssessmentRow } from "../../components/AssessmentHistory/finished_assessment_row.js";
import {
  CurrentAssessments,
  type AssessmentStarting,
  type CurrentAssessmentRow,
  type GuestAsking,
  type GuestEnding,
} from "../../components/CurrentAssessments/CurrentAssessments.js";
import type { DeletionOutcome } from "../../components/DeleteAssessmentDialog/DeleteAssessmentDialog.js";

/** The profile's Assessments tab: those under way, then their history. */
export const AssessmentsTab = ({
  state,
  rows,
  history,
  historyPaging,
  historyFailed,
  failed,
  starting,
  guestAsking,
  guestEnding,
  guestSharing,
  caseInvolvementHref,
  personName,
  readProgress,
  onDelete,
}: {
  state: PersonState | undefined;
  /** The assessments in the workflow, in order; see `currentAssessments`. */
  rows: CurrentAssessmentRow[] | undefined;
  /** The most recently finished assessments, in order; see `assessmentHistoryRows`. */
  history: FinishedAssessmentRow[] | undefined;
  /** Absent where `history` is the whole of it; see `AssessmentHistory`. */
  historyPaging?: HistoryPaging | undefined;
  /** Whether the history could not be worked out; see `AssessmentHistory`. */
  historyFailed: boolean;
  /** Whether the assessments in the workflow could not be read; see `CurrentAssessments`. */
  failed: boolean;
  /** Absent where the session may not start an assessment here. */
  starting?: AssessmentStarting | undefined;
  /** Absent where the session may not ask a guest to assess here. */
  guestAsking?: GuestAsking | undefined;
  /** Absent where the session may not end a guest's access here. */
  guestEnding?: GuestEnding | undefined;
  /** Absent where the session may not share assessments with a guest here. */
  guestSharing?: GuestSharing | undefined;
  caseInvolvementHref: string;
  personName: string | undefined;
  readProgress: (assessmentId: AssessmentId) => Promisable<ProgressReading>;
  onDelete: (deletion: {
    assessmentId: AssessmentId;
    request: DeleteAssessmentRequest;
  }) => Promisable<DeletionOutcome>;
}) => (
  <>
    <CurrentAssessments
      rows={rows}
      failed={failed}
      state={state}
      starting={starting}
      guestAsking={guestAsking}
      guestEnding={guestEnding}
      caseInvolvementHref={caseInvolvementHref}
      personName={personName}
      readProgress={readProgress}
      onDelete={onDelete}
    />

    <AssessmentHistory
      rows={history}
      failed={historyFailed}
      paging={historyPaging}
      sharing={guestSharing}
    />
  </>
);
